import React, { useEffect, useState, useCallback } from 'react';
import { apiGet } from '../../lib/api-client';
import { Card } from '../shared/Card';
import type { SessionListItem } from '@range-reader/shared';

interface SessionHistoryScreenProps {
  onViewSession: (sessionId: string) => void;
}

function formatDate(unix: number): string {
  return new Date(unix * 1000).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatTime(unix: number): string {
  return new Date(unix * 1000).toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit',
  });
}

function formatBB(cents: number): string {
  const dollars = cents / 100;
  if (dollars < 1) return `${cents}c`;
  return `$${dollars.toFixed(2).replace(/\.00$/, '')}`;
}

function ScorePill({ score }: { score: number }) {
  const color = score >= 70 ? 'score-pill-high' : score >= 40 ? 'score-pill-mid' : 'score-pill-low';
  return <span className={`score-pill ${color}`}>{score.toFixed(0)}</span>;
}

function HitBadge({ hit }: { hit: boolean }) {
  return (
    <span className={`hit-badge ${hit ? 'hit-badge-yes' : 'hit-badge-no'}`}>
      {hit ? 'HIT' : 'MISS'}
    </span>
  );
}

function CommunityCardsInline({ cards }: { cards: string }) {
  const parts = cards.split(' ').filter(Boolean);
  if (parts.length === 0) return <span className="history-no-board">-</span>;
  return (
    <div className="history-board-cards">
      {parts.map((c, i) => <Card key={i} card={c} small />)}
    </div>
  );
}

export function SessionHistoryScreen({ onViewSession }: SessionHistoryScreenProps) {
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('played_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [hitFilter, setHitFilter] = useState<'all' | 'hit' | 'miss'>('all');
  const pageSize = 20;

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        view: 'history',
        page: String(page),
        pageSize: String(pageSize),
        sort,
        order,
      };
      if (hitFilter === 'hit') params.hit = 'true';
      else if (hitFilter === 'miss') params.hit = 'false';

      const data = await apiGet<{ sessions: SessionListItem[]; total: number }>('/api/sessions', params);
      setSessions(data.sessions);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [page, sort, order, hitFilter]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  function handleSort(col: string) {
    if (sort === col) {
      setOrder(o => o === 'desc' ? 'asc' : 'desc');
    } else {
      setSort(col);
      setOrder('desc');
    }
    setPage(1);
  }

  function sortIndicator(col: string) {
    if (sort !== col) return '';
    return order === 'desc' ? ' \u25BC' : ' \u25B2';
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="history-screen">
      <div className="history-header">
        <h2>Session History</h2>
        <div className="history-filters">
          <select
            className="history-filter-select"
            value={hitFilter}
            onChange={e => { setHitFilter(e.target.value as 'all' | 'hit' | 'miss'); setPage(1); }}
          >
            <option value="all">All Results</option>
            <option value="hit">Hits Only</option>
            <option value="miss">Misses Only</option>
          </select>
          <span className="history-count">{total} session{total !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {loading && sessions.length === 0 && (
        <div className="history-loading">Loading sessions...</div>
      )}

      {!loading && sessions.length === 0 && (
        <div className="history-empty">
          <p>No sessions yet. Start training to build your history!</p>
        </div>
      )}

      {sessions.length > 0 && (
        <>
          <div className="history-table-wrap">
            <table className="history-table">
              <thead>
                <tr>
                  <th className="history-th-date" onClick={() => handleSort('played_at')}>
                    Date{sortIndicator('played_at')}
                  </th>
                  <th>Hand</th>
                  <th>Board</th>
                  <th>Result</th>
                  <th className="history-th-score" onClick={() => handleSort('accuracy_score')}>
                    Score{sortIndicator('accuracy_score')}
                  </th>
                  <th>Range</th>
                  <th>Isolation</th>
                  <th>Stakes</th>
                  <th>Note</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.id} className="history-row" onClick={() => onViewSession(s.id)}>
                    <td className="history-cell-date">
                      <div>{formatDate(s.playedAt)}</div>
                      <div className="history-time">{formatTime(s.playedAt)}</div>
                    </td>
                    <td className="history-cell-hand">
                      <div className="history-hole-cards">
                        <Card card={s.winnerHoleCards.split(' ')[0]} small />
                        <Card card={s.winnerHoleCards.split(' ')[1]} small />
                      </div>
                      <span className="history-hand-label">{s.actualHand}</span>
                    </td>
                    <td className="history-cell-board">
                      <CommunityCardsInline cards={s.communityCards} />
                    </td>
                    <td className="history-cell-result">
                      <HitBadge hit={s.hit} />
                    </td>
                    <td className="history-cell-score">
                      <ScorePill score={s.accuracyScore} />
                      <div className="history-score-detail">
                        {s.baseScore.toFixed(0)}+{s.nearMissCredit}+{s.streetBonus}
                      </div>
                    </td>
                    <td className="history-cell-range">
                      {s.finalRangeWidth}/{169}
                    </td>
                    <td className="history-cell-isolation">
                      {s.isolationStreet ? (
                        <span className={`history-street-badge street-${s.isolationStreet}`}>
                          {s.isolationStreet}
                        </span>
                      ) : (
                        <span className="history-no-isolation">-</span>
                      )}
                    </td>
                    <td className="history-cell-stakes">
                      {formatBB(s.bigBlind)} / {s.tableType}-max
                    </td>
                    <td className="history-cell-note">
                      {s.note ? (
                        <span className="history-note-icon" title={s.note}>
                          &#128221;
                        </span>
                      ) : null}
                    </td>
                    <td className="history-cell-action">
                      <button
                        className="history-view-btn"
                        onClick={e => { e.stopPropagation(); onViewSession(s.id); }}
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="history-pagination">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
              <span>Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
